import React, {
    createContext, useState, useEffect, useContext
} from 'react';
import {
    OrganizationContext
} from 'shared/context/OrganizationContext';
import {
    SubOrgContext
} from 'shared/context/SubOrganizationContext';
import {
    instructionService
} from 'shared/api/services/BEX/instruction.service';
import {
    modelService
} from 'shared/api/services/BEX/models.services';
import {
    mcpConnectionService
} from 'shared/api/services/BEX/mcpConnection.service';
import {
    jsonOpcodeService
} from 'shared/api/services/BEX/jsonOpcode.service';
import {
    GetLLMModelsRequest,
    InstructionEntity,
    LLMModelUnion,
    MCPConnectionEntity,
} from 'shared/api/services/bexService.types';
import {
    RedisJSONOpcode, StepKind
} from 'builders/builderOpcode/jsonMode/types';

export const V2_STEP_TYPES: { id: number; name: StepKind; label: string }[] = [
    {
 id: 1,
name: 'llm',
label: 'LLM'
},
    {
 id: 2,
name: 'rag',
label: 'RAG (Search)'
},
    {
 id: 3,
name: 'api',
label: 'API'
},
    {
 id: 4,
name: 'memory',
label: 'Memory'
},
    {
 id: 5,
name: 'choose',
label: 'Choose (Condition)'
},
    {
 id: 6,
name: 'subflow',
label: 'Subflow'
},
    {
 id: 7,
name: 'tool',
label: 'Tool'
},
];

export type V2OpCodeBuilderContextType = {
    opCodes: RedisJSONOpcode[];
    isOpcodeLoading: boolean;
    orgId: string | null;
    subOrgId: string | null;
    instructions: InstructionEntity[];
    modelProviders: LLMModelUnion[];
    handleReloadModels: (hardRefresh?: boolean) => Promise<LLMModelUnion[]>;
    IsInstructionLoading: boolean;
    actions: (action: { type: string; payload: any }) => void;
    fetchInstructions: () => Promise<void>;
    // A-0008.07 — MCP connections (Project O realm L1, used by `tool` step editor when protocol='mcp')
    mcpConnections: MCPConnectionEntity[];
    isMCPConnectionLoading: boolean;
    fetchMCPConnections: () => Promise<void>;
    // A-0008.06 — opcode list for the opcode-as-tool picker.
    fetchOpCodes: () => Promise<void>;
};

export const V2OpCodeBuilderContext = createContext<V2OpCodeBuilderContextType>({} as any);

export const V2OpCodeBuilderProvider = ({
    organizationId = null, subOrganizationId = null, children
}: {
    organizationId?: string | null,
    subOrganizationId?: string | null,
    children: React.ReactNode
}): React.ReactElement => {
    const [opCodes, setOpCodes] = useState<RedisJSONOpcode[]>([]);
    const [isOpcodeLoading, setIsOpcodeLoading] = useState<boolean>(false);
    const [instructions, setInstructions] = useState<InstructionEntity[]>([]);
    const [modelProviders, setModelProviders] = useState<LLMModelUnion[]>([]);
    const [IsInstructionLoading, setIsInstructionLoading] = useState<boolean>(true);
    // A-0008.07 — MCP-connection state.
    const [mcpConnections, setMcpConnections] = useState<MCPConnectionEntity[]>([]);
    const [isMCPConnectionLoading, setIsMCPConnectionLoading] = useState<boolean>(false);

    const {
 selectedOrganization
} = useContext(OrganizationContext) as any;
    const {
 selectedSubOrganization
} = useContext(SubOrgContext) as any;

    const orgId = organizationId ? organizationId : selectedOrganization?._id;
    const subOrgId = subOrganizationId ? subOrganizationId : selectedSubOrganization?._id;

    const actions = (action: { type: string; payload: any }) => {
        const {
 type, payload
} = action;
        switch (type) {
            case 'SET_OP_CODES':
                return setOpCodes(payload);
            case 'SET_IS_OPCODE_LOADING':
                return setIsOpcodeLoading(payload);
            default:
                console.error(`Unhandled action type: ${type}`);
                return null;
        }
    };

    const handleReloadModels = async (hardRefresh = false) => {
        const body: GetLLMModelsRequest = {
            provider: selectedOrganization.providers || [],
            filter: {
                Watson: {
 region: 'All',
showInactive: false
},
                Groq: {
 showInactive: false
}
            }
        };
        const res = await modelService.getModels(body, hardRefresh, orgId);
        const models = res.data || [];
        models.sort((a, b) => {
            const providerComparison = a.provider_type.localeCompare(b.provider_type);
            return providerComparison !== 0 ? providerComparison : a.model_id.localeCompare(b.model_id);
        });
        setModelProviders(models);
        return models;
    };

    const fetchInstructions = async () => {
        try {
            setInstructions([]);
            setIsInstructionLoading(true);

            const models = await handleReloadModels();
            const modelIdSet = new Set(models.map(model => model.model_id));

            const res = await instructionService.listModels(orgId, subOrgId);
            const instructios = res.data || [];
            const fetchedInstructions = instructios.map((instruction) => {
                const modifiedByMember = selectedOrganization.members.find(
                    member => member.id._id === instruction.modified_by
                );
                const userIdMember = selectedOrganization.members.find(
                    member => member.id._id === instruction.createdBy
                );

                const parameters = instruction.parameters || {};
                if (
                    instruction.model_type === 'IBM' &&
                    parameters &&
                    'decoding_method' in parameters &&
                    (parameters as { decoding_method?: string }).decoding_method === 'sampling'
                ) {
                    (parameters as { decoding_method?: string }).decoding_method = 'sample';
                }

                return {
                    ...instruction,
                    parameters,
                    modified_by: modifiedByMember ? modifiedByMember.id.name : instruction.modified_by,
                    user_id: userIdMember ? userIdMember.id.name : instruction.createdBy,
                    isModelProviderExist: modelIdSet.has(instruction.modelId)
                };
            });

            setInstructions(fetchedInstructions);
        } catch (error) {
            console.error('Error fetching instructions:', error);
        } finally {
            setIsInstructionLoading(false);
        }
    };

    // A-0008.06 — fetch all opcodes for the current (org, sub). Used by
    // V2StepTool's opcode picker (opcode_as_tool protocol) to display the
    // list of callable opcodes and auto-populate the inputs map from the
    // selected opcode's TypedInput[] declaration.
    const fetchOpCodes = async () => {
        if (!orgId || !subOrgId) return;
        try {
            setIsOpcodeLoading(true);
            const res = await jsonOpcodeService.listJSONOpcodes({
 organizationId: orgId,
subOrganizationId: subOrgId
});
            const list: any = (res as any)?.data ?? res;
            setOpCodes(Array.isArray(list) ? list : []);
        } catch (error) {
            console.error('Error fetching opcodes:', error);
        } finally {
            setIsOpcodeLoading(false);
        }
    };

    // A-0008.07 — fetch MCP connections for the current (org, sub).
    const fetchMCPConnections = async () => {
        if (!orgId || !subOrgId) return;
        try {
            setIsMCPConnectionLoading(true);
            const list = await mcpConnectionService.listMCPConnections(orgId, subOrgId);
            setMcpConnections(list || []);
        } catch (error) {
            console.error('Error fetching MCP connections:', error);
        } finally {
            setIsMCPConnectionLoading(false);
        }
    };

    useEffect(() => {
        fetchInstructions();
    }, []);

    useEffect(() => {
        if (orgId && subOrgId) {
            fetchMCPConnections();
            fetchOpCodes();
        }
    }, [orgId, subOrgId]);

    const value = {
        opCodes,
        isOpcodeLoading,
        orgId,
        subOrgId,
        instructions,
        modelProviders,
        handleReloadModels,
        IsInstructionLoading,
        actions,
        fetchInstructions,
        // A-0008.07
        mcpConnections,
        isMCPConnectionLoading,
        fetchMCPConnections,
        fetchOpCodes,
    };

    return (
        <V2OpCodeBuilderContext.Provider value={value}>
            {children}
        </V2OpCodeBuilderContext.Provider>
    );
};
